import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  RiCalendarCheckLine,
  RiCalendarLine,
  RiCalendar2Line,
  RiUserUnfollowLine,
  RiSuitcaseLine,
  RiMoneyRupeeCircleLine,
  RiHandCoinLine,
  RiRefreshLine,
  RiCloseLine,
  RiErrorWarningLine,
  RiFileList3Line,
  RiArrowDownSLine,
  RiCurrencyLine,
} from "react-icons/ri";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const BASE_URL = `${process.env.REACT_APP_API_URL}/salary-report`;
const ADMIN_BASE_URL = `${process.env.REACT_APP_API_URL}/admin`;

const Spinner = ({ className = "w-4 h-4" }) => (
  <AiOutlineLoading3Quarters className={`animate-spin ${className}`} />
);

const Toast = ({ message, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-medium bg-red-50 border border-red-200 text-red-800">
      <RiErrorWarningLine className="w-4 h-4 text-red-500" />
      <span>{message}</span>
      <button
        onClick={onClose}
        className="ml-2 opacity-50 hover:opacity-100 transition"
      >
        <RiCloseLine className="w-4 h-4" />
      </button>
    </div>
  );
};

const formatCurrency = (val) =>
  `₹${Number(val || 0)?.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const formatNumber = (val) => Number(val || 0)?.toLocaleString("en-IN");

const StatCard = ({ label, value, tone = "blue" }) => {
  const tones = {
    blue: "bg-blue-50 border-blue-100 text-blue-500 [&_?.val]:text-slate-800",
    emerald:
      "bg-emerald-50 border-emerald-100 text-emerald-500 [&_?.val]:text-emerald-700",
    red: "bg-red-50 border-red-100 text-red-400 [&_?.val]:text-red-600",
    amber:
      "bg-amber-50 border-amber-100 text-amber-500 [&_?.val]:text-amber-700",
  };
  return (
    <div className={`px-4 py-3 rounded-lg border ${tones[tone]}`}>
      <p className="text-[11px] uppercase font-semibold tracking-wide">
        {label}
      </p>
      <p className="val text-base font-bold mt-0?.5">{value}</p>
    </div>
  );
};

const EmptyState = ({ message }) => (
  <div className="py-16 text-center">
    <div className="w-14 h-14 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center mx-auto mb-3">
      <RiFileList3Line className="w-7 h-7 text-blue-300" />
    </div>
    <span className="text-sm text-slate-400">{message}</span>
  </div>
);

const LoadingRows = ({ label }) => (
  <div className="py-16 flex flex-col items-center gap-3 text-blue-400">
    <Spinner className="w-7 h-7 text-blue-500" />
    <span className="text-sm">{label}</span>
  </div>
);

const Th = ({ children, align = "left" }) => (
  <th className={`px-4 py-3 text-${align}`}>
    <span className="text-xs font-semibold text-blue-500 uppercase tracking-widest whitespace-nowrap">
      {children}
    </span>
  </th>
);

const Td = ({ children, align = "left", className = "" }) => (
  <td className={`px-4 py-3 text-${align} whitespace-nowrap ${className}`}>
    {children}
  </td>
);

const Table = ({ headers, children }) => (
  <div className="overflow-x-auto border border-blue-100 rounded-xl">
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-blue-50/50 border-b border-blue-100">
          {headers?.map((h) => (
            <Th key={h?.label || h} align={h?.align}>
              {h?.label || h}
            </Th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

const StatusPill = ({ status }) => {
  const map = {
    present: "bg-emerald-50 text-emerald-600 border-emerald-100",
    absent: "bg-red-50 text-red-500 border-red-100",
    pending: "bg-amber-50 text-amber-600 border-amber-100",
    processed: "bg-blue-50 text-blue-600 border-blue-100",
    paid: "bg-emerald-50 text-emerald-600 border-emerald-100",
    active: "bg-amber-50 text-amber-600 border-amber-100",
    "fully-paid": "bg-emerald-50 text-emerald-600 border-emerald-100",
  };
  return (
    <span
      className={`px-2 py-0?.5 rounded-md text-xs font-semibold border capitalize ${
        map[status] || "bg-slate-50 text-slate-500 border-slate-100"
      }`}
    >
      {status || "—"}
    </span>
  );
};

const MONTH_NAMES = Array?.from({ length: 12 }, (_, i) =>
  new Date(2000, i)?.toLocaleString("default", { month: "long" }),
);

const TABS = [
  {
    key: "attendance-daily",
    label: "Daily Attendance",
    icon: RiCalendarCheckLine,
  },
  {
    key: "attendance-monthly",
    label: "Monthly Attendance",
    icon: RiCalendarLine,
  },
  {
    key: "attendance-yearly",
    label: "Yearly Attendance",
    icon: RiCalendar2Line,
  },
  { key: "absentee", label: "Absentee Report", icon: RiUserUnfollowLine },
  { key: "leave-management", label: "Leave Management", icon: RiSuitcaseLine },
  { key: "salary", label: "Salary Report", icon: RiCurrencyLine },
  { key: "advance-salary", label: "Advance Salary", icon: RiHandCoinLine },
];

const todayStr = () => new Date()?.toISOString()?.slice(0, 10);

export default function SalaryAndAttendanceReport() {
  const [activeTab, setActiveTab] = useState("attendance-daily");
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const now = new Date();

  const [date, setDate] = useState(todayStr());
  const [month, setMonth] = useState(now?.getMonth() + 1);
  const [year, setYear] = useState(now?.getFullYear());
  const [staffId, setStaffId] = useState("");
  const [status, setStatus] = useState("");
  const [absenteeMode, setAbsenteeMode] = useState("daily"); // daily | monthly

  const [staffList, setStaffList] = useState([]);

  const notify = (message) => setToast({ message });

  const fetchStaffList = async () => {
    try {
      const res = await axios?.get(`${ADMIN_BASE_URL}/all-users`);
      const users = res?.data?.data || res?.data || [];
      setStaffList(users?.filter((u) => u?.userType === "staff"));
    } catch {
      // Non-fatal: filters fall back to manual staff ID entry
    }
  };

  useEffect(() => {
    fetchStaffList();
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setReportData(null);
    try {
      let url = "";
      const params = {};

      switch (activeTab) {
        case "attendance-daily":
          url = `${BASE_URL}/attendance/daily`;
          params.date = date;
          if (staffId) params.staffId = staffId;
          break;
        case "attendance-monthly":
          url = `${BASE_URL}/attendance/monthly/${year}/${month}`;
          if (staffId) params.staffId = staffId;
          break;
        case "attendance-yearly":
          url = `${BASE_URL}/attendance/yearly/${year}`;
          if (staffId) params.staffId = staffId;
          break;
        case "absentee":
          url = `${BASE_URL}/absentee`;
          if (absenteeMode === "daily") {
            params.date = date;
          } else {
            params.month = month;
            params.year = year;
          }
          break;
        case "leave-management":
          url = `${BASE_URL}/leave-management`;
          params.month = month;
          params.year = year;
          if (staffId) params.staffId = staffId;
          break;
        case "salary":
          url = `${BASE_URL}/salary/${month}/${year}`;
          if (staffId) params.staffId = staffId;
          if (status) params.status = status;
          break;
        case "advance-salary":
          url = `${BASE_URL}/advance-salary`;
          if (staffId) params.staffId = staffId;
          break;
        default:
          break;
      }

      const res = await axios?.get(url, { params });
      setReportData(res?.data);
    } catch (err) {
      notify(err?.response?.data?.message || "Failed to load report");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, date, month, year, staffId, status, absenteeMode]);

  useEffect(() => {
    setExpandedRows(new Set());
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const clearFilters = () => {
    setDate(todayStr());
    setMonth(now?.getMonth() + 1);
    setYear(now?.getFullYear());
    setStaffId("");
    setStatus("");
    setAbsenteeMode("daily");
  };

  const toggleExpand = (key) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next?.has(key) ? next?.delete(key) : next?.add(key);
      return next;
    });
  };

  /* ---------------------------------------------------------------- */
  /*  Tab-specific renderers                                            */
  /* ---------------------------------------------------------------- */

  const renderAttendanceDaily = () => {
    if (!reportData) return null;
    const rows = reportData?.data || [];
    const summary = reportData?.summary || {};

    return (
      <>
        <div className="grid grid-cols-3 gap-3 mb-5">
          <StatCard
            label="Total Staff"
            value={summary?.totalStaff ?? rows?.length}
          />
          <StatCard
            label="Present"
            value={summary?.present ?? 0}
            tone="emerald"
          />
          <StatCard label="Absent" value={summary?.absent ?? 0} tone="red" />
        </div>
        {rows?.length === 0 ? (
          <EmptyState message="No attendance data found for this date?." />
        ) : (
          <Table
            headers={["Name", "Position", "Status", "Check-ins", "Work Hours"]}
          >
            {rows?.map((r) => (
              <tr
                key={r?.staffId}
                className="border-b border-blue-50 hover:bg-blue-50/40 transition"
              >
                <Td className="font-semibold text-slate-800">{r?.name}</Td>
                <Td className="text-slate-500">{r?.position || "—"}</Td>
                <Td>
                  <StatusPill status={r?.status} />
                </Td>
                <Td>{r?.totalCheckIns}</Td>
                <Td>{r?.totalWorkHours}</Td>
              </tr>
            ))}
          </Table>
        )}
      </>
    );
  };

  const renderAttendanceMonthly = () => {
    if (!reportData) return null;
    const rows = reportData?.data || [];
    const meta = reportData?.metadata || {};

    return (
      <>
        <div className="grid grid-cols-3 gap-3 mb-5">
          <StatCard
            label="Total Staff"
            value={meta?.totalStaff ?? rows?.length}
          />
          <StatCard
            label="Days In Month"
            value={meta?.totalDaysInMonth ?? "—"}
            tone="blue"
          />
          <StatCard
            label="Period"
            value={`${MONTH_NAMES[(meta?.month || month) - 1]} ${meta?.year || year}`}
            tone="amber"
          />
        </div>
        {rows?.length === 0 ? (
          <EmptyState message="No attendance data found for this month?." />
        ) : (
          <Table
            headers={[
              "",
              "Name",
              "Position",
              "Present",
              "Absent",
              "Work Hours",
              "Attendance %",
            ]}
          >
            {rows?.map((r) => {
              const isOpen = expandedRows?.has(r?.staffId);
              return (
                <React.Fragment key={r?.staffId}>
                  <tr
                    className="border-b border-blue-50 hover:bg-blue-50/40 transition cursor-pointer"
                    onClick={() => toggleExpand(r?.staffId)}
                  >
                    <Td>
                      <RiArrowDownSLine
                        className={`w-4 h-4 text-blue-300 transition-transform ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </Td>
                    <Td className="font-semibold text-slate-800">{r?.name}</Td>
                    <Td className="text-slate-500">{r?.position || "—"}</Td>
                    <Td className="text-emerald-600 font-medium">
                      {r?.summary?.presentDays}
                    </Td>
                    <Td className="text-red-500 font-medium">
                      {r?.summary?.absentDays}
                    </Td>
                    <Td>{r?.summary?.totalWorkHours}</Td>
                    <Td>{r?.summary?.attendancePercentage}%</Td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={7} className="bg-blue-50/30 px-6 py-3">
                        <div className="flex flex-wrap gap-1?.5">
                          {r?.days?.map((d) => (
                            <span
                              key={d?.date}
                              title={`${d?.date}${d?.isHoliday ? " (Holiday)" : d?.isWeekend ? " (Weekend)" : ""}`}
                              className={`w-7 h-7 flex items-center justify-center text-[10px] rounded-md border font-semibold ${
                                d?.status === "present"
                                  ? "bg-emerald-50 border-emerald-200 text-emerald-600"
                                  : d?.isWeekend || d?.isHoliday
                                    ? "bg-slate-50 border-slate-200 text-slate-400"
                                    : "bg-red-50 border-red-200 text-red-500"
                              }`}
                            >
                              {d?.date?.slice(-2)}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </Table>
        )}
      </>
    );
  };

  const renderAttendanceYearly = () => {
    if (!reportData) return null;
    const rows = reportData?.data || [];
    const meta = reportData?.metadata || {};

    return (
      <>
        <div className="grid grid-cols-2 gap-3 mb-5">
          <StatCard
            label="Total Staff"
            value={meta?.totalStaff ?? rows?.length}
          />
          <StatCard label="Year" value={meta?.year || year} tone="amber" />
        </div>
        {rows?.length === 0 ? (
          <EmptyState message="No attendance data found for this year?." />
        ) : (
          <Table
            headers={[
              "",
              "Name",
              "Position",
              "Present Days",
              "Absent Days",
              "Work Hours",
            ]}
          >
            {rows?.map((r) => {
              const isOpen = expandedRows?.has(r?.staffId);
              return (
                <React.Fragment key={r?.staffId}>
                  <tr
                    className="border-b border-blue-50 hover:bg-blue-50/40 transition cursor-pointer"
                    onClick={() => toggleExpand(r?.staffId)}
                  >
                    <Td>
                      <RiArrowDownSLine
                        className={`w-4 h-4 text-blue-300 transition-transform ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </Td>
                    <Td className="font-semibold text-slate-800">{r?.name}</Td>
                    <Td className="text-slate-500">{r?.position || "—"}</Td>
                    <Td className="text-emerald-600 font-medium">
                      {r?.yearlyTotals?.presentDays}
                    </Td>
                    <Td className="text-red-500 font-medium">
                      {r?.yearlyTotals?.absentDays}
                    </Td>
                    <Td>{r?.yearlyTotals?.totalWorkHours}</Td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={6} className="bg-blue-50/30 px-6 py-3">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-blue-400 uppercase tracking-widest">
                              <th className="text-left py-1 pr-4">Month</th>
                              <th className="text-left py-1 pr-4">Present</th>
                              <th className="text-left py-1 pr-4">Absent</th>
                              <th className="text-left py-1">Work Hours</th>
                            </tr>
                          </thead>
                          <tbody>
                            {r?.monthlyBreakdown?.map((m) => (
                              <tr
                                key={m?.month}
                                className="border-t border-blue-100/60"
                              >
                                <td className="py-1 pr-4 font-medium text-slate-700">
                                  {m?.monthName}
                                </td>
                                <td className="py-1 pr-4 text-emerald-600">
                                  {m?.presentDays}
                                </td>
                                <td className="py-1 pr-4 text-red-500">
                                  {m?.absentDays}
                                </td>
                                <td className="py-1">{m?.totalWorkHours}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </Table>
        )}
      </>
    );
  };

  const renderAbsentee = () => {
    if (!reportData) return null;

    if (absenteeMode === "daily") {
      const rows = reportData?.data || [];
      return (
        <>
          <div className="grid grid-cols-2 gap-3 mb-5">
            <StatCard label="Date" value={reportData?.date || date} />
            <StatCard
              label="Total Absent"
              value={reportData?.totalAbsent ?? rows?.length}
              tone="red"
            />
          </div>
          {rows?.length === 0 ? (
            <EmptyState message="No absentees found for this date?." />
          ) : (
            <Table headers={["Name", "Position"]}>
              {rows?.map((r) => (
                <tr
                  key={r?.staffId}
                  className="border-b border-blue-50 hover:bg-blue-50/40 transition"
                >
                  <Td className="font-semibold text-slate-800">{r?.name}</Td>
                  <Td className="text-slate-500">{r?.position || "—"}</Td>
                </tr>
              ))}
            </Table>
          )}
        </>
      );
    }

    const rows = reportData?.data || [];
    return (
      <>
        <div className="grid grid-cols-2 gap-3 mb-5">
          <StatCard
            label="Period"
            value={`${MONTH_NAMES[(reportData?.metadata?.month || month) - 1]} ${reportData?.metadata?.year || year}`}
          />
          <StatCard
            label="Staff With Absences"
            value={reportData?.totalStaffWithAbsences ?? rows?.length}
            tone="red"
          />
        </div>
        {rows?.length === 0 ? (
          <EmptyState message="No absences found for this month?." />
        ) : (
          <Table headers={["", "Name", "Position", "Total Absent Days"]}>
            {rows?.map((r) => {
              const isOpen = expandedRows?.has(r?.staffId);
              return (
                <React.Fragment key={r.staffId}>
                  <tr
                    className="border-b border-blue-50 hover:bg-blue-50/40 transition cursor-pointer"
                    onClick={() => toggleExpand(r?.staffId)}
                  >
                    <Td>
                      <RiArrowDownSLine
                        className={`w-4 h-4 text-blue-300 transition-transform ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </Td>
                    <Td className="font-semibold text-slate-800">{r?.name}</Td>
                    <Td className="text-slate-500">{r?.position || "—"}</Td>
                    <Td className="text-red-500 font-medium">
                      {r?.totalAbsentDays}
                    </Td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={4} className="bg-blue-50/30 px-6 py-3">
                        <div className="flex flex-wrap gap-1?.5">
                          {r?.absentDates?.map((d) => (
                            <span
                              key={d}
                              className="px-2 py-1 rounded-md text-xs font-medium bg-red-50 border border-red-200 text-red-500"
                            >
                              {d}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </Table>
        )}
      </>
    );
  };

  const renderLeaveManagement = () => {
    if (!reportData) return null;
    const rows = reportData?.data || [];

    return (
      <>
        {reportData?.note && (
          <div className="mb-4 px-4 py-2?.5 rounded-lg bg-amber-50 border border-amber-100 text-amber-700 text-xs">
            {reportData?.note}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 mb-5">
          <StatCard
            label="Period"
            value={`${MONTH_NAMES[(reportData?.metadata?.month || month) - 1]} ${reportData?.metadata?.year || year}`}
          />
          <StatCard label="Staff Shown" value={rows?.length} tone="blue" />
        </div>
        {rows?.length === 0 ? (
          <EmptyState message="No leave data found for this month?." />
        ) : (
          <Table
            headers={[
              "Name",
              "Position",
              "Leave Taken",
              "Holidays Not Worked",
              "Leave Balance",
            ]}
          >
            {rows?.map((r) => (
              <tr
                key={r?.staffId}
                className="border-b border-blue-50 hover:bg-blue-50/40 transition"
              >
                <Td className="font-semibold text-slate-800">{r?.name}</Td>
                <Td className="text-slate-500">{r?.position || "—"}</Td>
                <Td className="text-red-500 font-medium">
                  {r?.leaveTakenThisMonth}
                </Td>
                <Td>{r?.holidaysNotWorked}</Td>
                <Td className="text-slate-400 italic">
                  {r?.leaveBalance === null ? "N/A" : r?.leaveBalance}
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </>
    );
  };

  const renderSalary = () => {
    if (!reportData) return null;
    const rows = reportData?.data || [];
    const summary = reportData?.summary || {};

    return (
      <>
        <div className="grid grid-cols-4 gap-3 mb-5">
          <StatCard label="Staff" value={summary?.totalStaff ?? rows?.length} />
          <StatCard
            label="Total Base Salary"
            value={formatCurrency(summary?.totalBaseSalary)}
            tone="blue"
          />
          <StatCard
            label="Total Payable"
            value={formatCurrency(summary?.totalCalculatedSalary)}
            tone="emerald"
          />
          <StatCard
            label="Advance Deductions"
            value={formatCurrency(summary?.totalAdvanceDeductions)}
            tone="amber"
          />
        </div>
        {rows?.length === 0 ? (
          <EmptyState message="No salary records found for this period?." />
        ) : (
          <Table
            headers={[
              "Name",
              "Position",
              "Base Salary",
              "Work Days",
              "Absent",
              "Advance Ded?.",
              "Net Salary",
              "Status",
            ]}
          >
            {rows?.map((r) => (
              <tr
                key={r?.staffId}
                className="border-b border-blue-50 hover:bg-blue-50/40 transition"
              >
                <Td className="font-semibold text-slate-800">{r?.name}</Td>
                <Td className="text-slate-500">{r?.position || "—"}</Td>
                <Td>{formatCurrency(r?.baseSalary)}</Td>
                <Td>{r?.totalWorkDays}</Td>
                <Td className="text-red-500">{r?.absentDays}</Td>
                <Td className="text-amber-600">
                  {formatCurrency(r?.advanceDeduction)}
                </Td>
                <Td className="text-emerald-600 font-medium">
                  {formatCurrency(r?.calculatedSalary)}
                </Td>
                <Td>
                  <StatusPill status={r?.status} />
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </>
    );
  };

  const renderAdvanceSalary = () => {
    if (!reportData) return null;
    const rows = reportData?.data || [];
    const summary = reportData?.summary || {};

    return (
      <>
        <div className="grid grid-cols-4 gap-3 mb-5">
          <StatCard
            label="Advances Issued"
            value={summary?.totalAdvancesIssued ?? rows?.length}
          />
          <StatCard
            label="Total Advance Amount"
            value={formatCurrency(summary?.totalAdvanceAmount)}
            tone="blue"
          />
          <StatCard
            label="Total Paid"
            value={formatCurrency(summary?.totalPaidAmount)}
            tone="emerald"
          />
          <StatCard
            label="Remaining"
            value={formatCurrency(summary?.totalRemainingAmount)}
            tone="amber"
          />
        </div>
        {rows?.length === 0 ? (
          <EmptyState message="No advance salary records found?." />
        ) : (
          <Table
            headers={[
              "Name",
              "Position",
              "Advance Amount",
              "Monthly Pay",
              "Paid Months",
              "Remaining",
              "Status",
              "Reason",
            ]}
          >
            {rows?.map((r) => (
              <tr
                key={r?.advanceId}
                className="border-b border-blue-50 hover:bg-blue-50/40 transition"
              >
                <Td className="font-semibold text-slate-800">{r?.name}</Td>
                <Td className="text-slate-500">{r?.position || "—"}</Td>
                <Td>{formatCurrency(r?.advanceAmount)}</Td>
                <Td>{formatCurrency(r?.monthlyPayAmount)}</Td>
                <Td>{r?.paidMonths}</Td>
                <Td className="text-amber-600 font-medium">
                  {formatCurrency(r?.remaining)}
                </Td>
                <Td>
                  <StatusPill status={r?.status} />
                </Td>
                <Td className="text-slate-500 whitespace-normal">
                  {r?.reason || "—"}
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </>
    );
  };

  const renderActiveTab = () => {
    if (loading) return <LoadingRows label="Crunching the numbers…" />;
    switch (activeTab) {
      case "attendance-daily":
        return renderAttendanceDaily();
      case "attendance-monthly":
        return renderAttendanceMonthly();
      case "attendance-yearly":
        return renderAttendanceYearly();
      case "absentee":
        return renderAbsentee();
      case "leave-management":
        return renderLeaveManagement();
      case "salary":
        return renderSalary();
      case "advance-salary":
        return renderAdvanceSalary();
      default:
        return null;
    }
  };

  /* ---------------------------------------------------------------- */
  /*  Filter visibility per tab                                         */
  /* ---------------------------------------------------------------- */

  const showDate =
    activeTab === "attendance-daily" ||
    (activeTab === "absentee" && absenteeMode === "daily");
  const showMonthYear =
    activeTab === "attendance-monthly" ||
    activeTab === "leave-management" ||
    activeTab === "salary" ||
    (activeTab === "absentee" && absenteeMode === "monthly");
  const showYearOnly = activeTab === "attendance-yearly";
  const showAbsenteeMode = activeTab === "absentee";
  const showStaffFilter = activeTab !== "absentee";
  const showStatusFilter = activeTab === "salary";

  const yearOptions = Array?.from(
    { length: 6 },
    (_, i) => now?.getFullYear() - i,
  );

  return (
    <div className="min-h-screen bg-white text-slate-800">
      <header className="bg-white border-b border-blue-100 sticky top-0 z-30 shadow-sm">
        <div className="w-full mx-auto px-6 py-3?.5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-md shadow-blue-200">
              <RiSuitcaseLine className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-base font-bold text-blue-800 tracking-tight">
                HR & Payroll Reports
              </h1>
              <p className="text-[11px] text-blue-400 tracking-widest uppercase">
                Attendance · Absentee · Leave · Salary · Advance
              </p>
            </div>
          </div>
          <button
            onClick={fetchReport}
            disabled={loading}
            title="Refresh"
            className="w-9 h-9 flex items-center justify-center rounded-lg border border-blue-200 text-blue-400 hover:text-blue-600 hover:bg-blue-50 transition disabled:opacity-50"
          >
            <RiRefreshLine
              className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
            />
          </button>
        </div>

        {/* Tab strip */}
        <div className="w-full mx-auto px-6 flex gap-1 overflow-x-auto pb-2 pt-1">
          {TABS?.map((tab) => {
            const Icon = tab?.icon;
            const active = activeTab === tab?.key;
            return (
              <button
                key={tab?.key}
                onClick={() => setActiveTab(tab?.key)}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition ${
                  active
                    ? "bg-blue-600 text-white shadow-md shadow-blue-200"
                    : "text-blue-500 hover:bg-blue-50 border border-transparent"
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab?.label}
              </button>
            );
          })}
        </div>
      </header>

      <div className="w-full mx-auto px-6 pt-6 pb-10">
        {/* Filter bar */}
        <div className="bg-white border border-blue-100 rounded-2xl p-4 mb-5 shadow-sm flex items-end gap-4 flex-wrap">
          {showAbsenteeMode && (
            <div className="flex flex-col gap-1?.5">
              <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                View
              </label>
              <select
                value={absenteeMode}
                onChange={(e) => setAbsenteeMode(e?.target?.value)}
                className="bg-blue-50/60 border border-blue-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition appearance-none"
              >
                <option value="daily">Daily</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          )}

          {showDate && (
            <div className="flex flex-col gap-1?.5">
              <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                Date
              </label>
              <div className="relative">
                <RiCalendarLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300 pointer-events-none" />
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e?.target?.value)}
                  className="bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
                />
              </div>
            </div>
          )}

          {showMonthYear && (
            <div className="flex flex-col gap-1?.5">
              <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                Month
              </label>
              <select
                value={month}
                onChange={(e) => setMonth(parseInt(e?.target?.value, 10))}
                className="bg-blue-50/60 border border-blue-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition appearance-none"
              >
                {MONTH_NAMES?.map((m, i) => (
                  <option key={m} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
          )}

          {(showMonthYear || showYearOnly) && (
            <div className="flex flex-col gap-1?.5">
              <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                Year
              </label>
              <select
                value={year}
                onChange={(e) => setYear(parseInt(e?.target?.value, 10))}
                className="bg-blue-50/60 border border-blue-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition appearance-none"
              >
                {yearOptions?.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          )}

          {showStaffFilter && (
            <div className="flex flex-col gap-1?.5">
              <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                Staff
              </label>
              {staffList?.length > 0 ? (
                <select
                  value={staffId}
                  onChange={(e) => setStaffId(e?.target?.value)}
                  className="bg-blue-50/60 border border-blue-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition appearance-none min-w-[180px]"
                >
                  <option value="">All Staff</option>
                  {staffList?.map((s) => (
                    <option key={s?._id} value={s?._id}>
                      {s?.name}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  value={staffId}
                  onChange={(e) => setStaffId(e?.target?.value)}
                  placeholder="Staff ID (optional)"
                  className="bg-blue-50/60 border border-blue-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition min-w-[180px]"
                />
              )}
            </div>
          )}

          {showStatusFilter && (
            <div className="flex flex-col gap-1?.5">
              <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e?.target?.value)}
                className="bg-blue-50/60 border border-blue-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition appearance-none"
              >
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="processed">Processed</option>
                <option value="paid">Paid</option>
              </select>
            </div>
          )}

          <div className="flex gap-2 ml-auto">
            <button
              onClick={clearFilters}
              className="flex items-center gap-1?.5 px-4 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Clear
            </button>
            <button
              onClick={fetchReport}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white transition shadow-md shadow-blue-200 disabled:opacity-50"
            >
              Apply
            </button>
          </div>
        </div>

        {/* Report body */}
        <div className="bg-white border border-blue-100 rounded-2xl p-5 shadow-sm">
          {renderActiveTab()}
        </div>
      </div>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
